// Tout ce dont la page « Nouvelle demande » a besoin : les données (personnes, ateliers, programme, demandes existantes, absences, historique,
// réglages), l'état de la saisie (réducteur de etatDemande.ts), ce qui en découle (séances, compteurs, ce qu'il reste à faire, solde) et l'envoi.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRole } from '../../../context/RoleContext';
import { api, ApiError } from '../../../api/client';
import type { Absence, Atelier, ControleSolde, Demande, HistoriqueRemplacement, Personne, ResultatCreation, SeanceProgramme } from '../../../types';
import { lienTipee } from '../format';
import { jourLocalIso } from '../horaire';
import { useMaPersonneEtat } from '../useMaPersonne';
import { useMeta } from '../useMeta';
import { classer, peutEtrePrevenue, type Candidat } from './classementRemplacants';
import { addJours, dernierDuMois, moisDe, type DateIso } from './dates';
import { etatInitial, reduire, type Action, type ContexteReducteur, type EtatDemande } from './etatDemande';
import { joursVisibles } from './selection';
import {
  ateliersAjoutables, aUneAutreActivite, compteurs, corpsCreation, manques, seancesDesJours, seancesDuJour, type Compteurs, type EtatDonnees, type Manque, type Seance,
  type SourcesSeances,
} from './seancesDemande';
import { voix, type Voix } from './textesDemande';

export interface PrefillNouvelleDemande { absent?: string; date?: string; version?: string }

export interface Reglages { tipeeUrl: string; scopeCollegues: string; battementMin: number }
const REGLAGES_DEFAUT: Reglages = { tipeeUrl: '', scopeCollegues: 'tous', battementMin: 60 };

/** Ce que la confirmation montre : la réponse du serveur + les séances qui existaient déjà (jamais envoyées : le serveur ne les connaît pas). */
export type ResultatEnvoi = ResultatCreation & { dejaDemandees: Seance[] };

/** Les demandes déjà faites et les séances prises en charge d'UNE personne absente, sur la période [du, au] réellement chargée (`personne` = sa clé de chargement). */
export interface Existantes { personne: string; du: DateIso; au: DateIso; demandes: Demande[]; reservees: { atelier_id: number; date_atelier: DateIso }[] }
const AUCUNE: Existantes = { personne: '', du: '9999-12-31', au: '0000-01-01', demandes: [], reservees: [] };

const dernierJour = (jours: DateIso[]) => jours[jours.length - 1];

/** Une panne (500, réseau coupé) ne dit rien d'utile : on dit quoi faire. Les refus du serveur (400, 403…) sont écrits pour être lus tels quels. */
function texteErreur(err: unknown): string {
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) return err.message;
  return "L'envoi n'a pas abouti : vérifiez votre connexion et réessayez.";
}

export function useNouvelleDemande(prefill?: PrefillNouvelleDemande) {
  const { isAdmin, aRole } = useRole();
  const { meta } = useMeta();
  const aujourdhui = useMemo(() => jourLocalIso(), []);
  /** La gestion choisit la personne absente ; une personne intervenante ne demande que pour elle-même. */
  const peutChoisirAutrui = isAdmin || aRole('responsable', 'secretariat', 'rh', 'planification');

  // ── Référentiels ───────────────────────────────────────────────────────────────────────────────────────
  const [personnes, setPersonnes] = useState<Personne[]>([]);
  const [ateliers, setAteliers] = useState<Record<number, Atelier>>({});
  const [ateliersFinis, setAteliersFinis] = useState(false);   // la liste des ateliers est arrivée (ou a échoué : l'alerte le dit)
  const [reglages, setReglages] = useState<Reglages>(REGLAGES_DEFAUT);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);
  useEffect(() => {
    api.getPersonnes({ actif: true }).then(setPersonnes).catch(() => setErreurChargement('Impossible de charger la liste des personnes.'));
    api.getAteliers({ actif: true })
      .then((l) => setAteliers(Object.fromEntries(l.map((a) => [a.id, a]))))
      .catch(() => setErreurChargement('Impossible de charger la liste des ateliers.'))
      .finally(() => setAteliersFinis(true));
    api.getSettings().then((s) => {
      const b = Number(s['tipee.battement_minutes']);
      setReglages({
        tipeeUrl: lienTipee(s['tipee.instance_url']),
        scopeCollegues: s['tableau.scope_collegues'] ?? 'tous',
        battementMin: s['tipee.battement_minutes'] != null && Number.isFinite(b) ? Math.max(0, b) : 60,
      });
    }).catch((err: unknown) => console.error('[nouvelle demande] réglages', err));   // sans eux : pas de lien « Ouvrir Tipee », 60 min de battement
  }, []);
  const personnesParId = useMemo(() => Object.fromEntries(personnes.map((p) => [p.id, p])) as Record<number, Personne>, [personnes]);
  const { personne: maPersonne, chargement: chargementMoi } = useMaPersonneEtat(personnes);

  // ── État de la saisie ──────────────────────────────────────────────────────────────────────────────────
  const absentPrefill = peutChoisirAutrui && prefill?.absent && !Number.isNaN(Number(prefill.absent)) ? Number(prefill.absent) : null;
  const [etat, setEtat] = useState<EtatDemande>(() => etatInitial({ aujourdhui, absentId: absentPrefill, date: prefill?.date ?? null }));
  const etatRef = useRef(etat);
  etatRef.current = etat;
  const ctxRef = useRef<ContexteReducteur>({ aujourdhui, seances: [], personnes: {} });
  const dispatch = useCallback((a: Action) => setEtat((e) => reduire(e, a, ctxRef.current)), []);
  const apresRendu = useCallback((fn: () => void) => { requestAnimationFrame(() => fn()); }, []);
  const etatCourant = useCallback(() => etatRef.current, []);

  // Une personne intervenante demande pour elle-même : la fiche du compte est choisie d'office (sans message : rien n'a changé pour elle).
  useEffect(() => {
    if (peutChoisirAutrui || !maPersonne) return;
    setEtat((e) => (e.absentId === maPersonne.id ? e : { ...e, absentId: maPersonne.id }));
  }, [peutChoisirAutrui, maPersonne]);

  const absentId = etat.absentId;
  const pourMoiRef = useRef(false);
  const personneAbsente = absentId != null ? personnesParId[absentId] ?? null : null;
  // Une personne intervenante demande toujours pour elle-même (le serveur l'impose) : « vous », même avant que sa fiche soit arrivée.
  const pourMoi = !peutChoisirAutrui || (absentId != null && maPersonne != null && absentId === maPersonne.id);
  pourMoiRef.current = pourMoi;
  const laVoix: Voix = { pourMoi, prenom: personneAbsente?.prenom ?? '', nomComplet: personneAbsente?.display_name ?? '' };
  const dire = useCallback((vous: string, tiers: string) => voix(laVoix, vous, tiers), [laVoix.pourMoi, laVoix.prenom]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Programme (séances de toute l'institution), par mois, mis en cache ─────────────────────────────────
  const [progParMois, setProgParMois] = useState<Record<string, SeanceProgramme[]>>({});
  const [erreurMois, setErreurMois] = useState<Record<string, true>>({});
  const enCoursMois = useRef(new Set<string>());
  const moisNecessaires = useMemo(() => {
    const s = new Set<string>();
    for (const d of joursVisibles(etat.mois)) s.add(moisDe(d));
    for (const d of etat.jours) s.add(moisDe(d));
    return Array.from(s).sort();
  }, [etat.mois, etat.jours]);
  const clesMois = moisNecessaires.join(',');
  const [essaiMois, setEssaiMois] = useState(0);
  useEffect(() => {
    for (const m of moisNecessaires) {
      if (progParMois[m] || enCoursMois.current.has(m)) continue;
      enCoursMois.current.add(m);
      api.getSeances({ du: `${m}-01`, au: dernierDuMois(`${m}-01`) })
        .then((s) => { setProgParMois((p) => ({ ...p, [m]: s })); setErreurMois((x) => { const { [m]: _omis, ...reste } = x; return reste; }); })
        .catch(() => setErreurMois((x) => ({ ...x, [m]: true })))
        .finally(() => enCoursMois.current.delete(m));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clesMois, essaiMois]);
  const programme = useMemo(() => Object.values(progParMois).flat(), [progParMois]);
  const moisEnErreur = moisNecessaires.filter((m) => erreurMois[m] && !progParMois[m]);
  const rechargerProgramme = useCallback(() => setEssaiMois((n) => n + 1), []);
  /** Les séances d'un jour sont-elles connues ? (sinon « Pas d'atelier ce jour-là » serait faux : on dit « chargement » ou « indisponible ») */
  const programmeDuJour = useCallback((d: DateIso): 'ok' | 'chargement' | 'erreur' => {
    const m = moisDe(d);
    if (progParMois[m] && ateliersFinis) return 'ok';
    return erreurMois[m] ? 'erreur' : 'chargement';
  }, [progParMois, erreurMois, ateliersFinis]);

  // ── Demandes déjà faites, séances réservées, absences : sur la période choisie ─────────────────────────
  const [existantes, setExistantes] = useState<Existantes>(AUCUNE);
  const [absences, setAbsences] = useState<Absence[]>([]);
  /** Ce qui n'a pas pu être chargé : les demandes déjà faites (bloque l'envoi), les absences (« disponible » ne se dit plus). */
  const [echec, setEchec] = useState({ demandes: false, absences: false });
  const [versionDonnees, setVersionDonnees] = useState(0);
  const du = etat.jours[0];
  const au = dernierJour(etat.jours);
  const clePersonne = absentId != null ? `${absentId}|${versionDonnees}|${pourMoi ? 'moi' : 'autre'}` : '';

  // Les demandes déjà faites et les séances prises en charge se chargent UNE fois par personne, d'aujourd'hui à deux ans plus loin (plus loin si un jour choisi l'est) : dès que la
  // personne est connue, avant même le premier jour choisi. Ajouter ou retirer un jour ne rappelle donc rien et ne fait rien clignoter.
  const horizon = useMemo(() => addJours(aujourdhui, 730), [aujourdhui]);
  const finDemandes = au && au > horizon ? au : horizon;
  const cleDemandes = clePersonne ? `${clePersonne}|${finDemandes}` : '';
  useEffect(() => {
    if (!cleDemandes) { setExistantes(AUCUNE); setEchec((x) => ({ ...x, demandes: false })); return; }
    let annule = false;
    Promise.all([
      // Toutes les demandes non supprimées de la personne (terminées comprises : l'index unique des demandes actives les compte aussi).
      // Pour soi : la liste « miennes » (états enrichis : où en est la recherche, retour transmis…) ; pour une autre personne : la liste habituelle.
      (pourMoiRef.current ? api.getDemandes({ vue: 'miennes', du: aujourdhui, au: finDemandes }) : api.getDemandes({ personne_id: absentId!, du: aujourdhui, au: finDemandes })).catch(() => null),
      api.getSeancesReservees({ du: aujourdhui, au: finDemandes }).catch(() => null),
    ]).then(([demandes, reservees]) => {
      if (annule) return;
      if (demandes && reservees) setExistantes({ personne: clePersonne, du: aujourdhui, au: finDemandes, demandes, reservees });
      setEchec((x) => ({ ...x, demandes: !(demandes && reservees) }));
    });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleDemandes]);

  // Les absences, elles, concernent toute l'équipe : seulement sur la période choisie.
  const cleAbsences = du ? `${du}|${au}|${versionDonnees}` : '';
  useEffect(() => {
    if (!cleAbsences) { setAbsences([]); setEchec((x) => ({ ...x, absences: false })); return; }
    let annule = false;
    api.getAbsences({ du, au })
      .then((abs) => { if (!annule) { setAbsences(abs); setEchec((x) => ({ ...x, absences: false })); } })
      .catch(() => { if (!annule) { setAbsences([]); setEchec((x) => ({ ...x, absences: true })); } });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleAbsences]);
  // Les demandes d'une autre personne ne comptent jamais pour celle-ci, le temps que les siennes arrivent.
  const existantesActives = existantes.personne === clePersonne ? existantes : AUCUNE;
  const existantesCouvrent = etat.jours.every((d) => d >= existantesActives.du && d <= existantesActives.au);

  // ── Séances ────────────────────────────────────────────────────────────────────────────────────────────
  const sources: SourcesSeances = useMemo(() => ({
    absentId, seances: programme, ateliers, existantes: existantesActives.demandes, reservees: existantesActives.reservees, ajoutees: etat.ajoutees, pourMoi,
  }), [absentId, programme, ateliers, existantesActives, etat.ajoutees, pourMoi]);
  const seancesChoisies = useMemo(() => seancesDesJours(sources, etat.jours), [sources, etat.jours]);
  /** Séances d'un jour (calendrier : tous les jours visibles ; panneau : les jours choisis). */
  const seancesDuJourMemo = useMemo(() => {
    const cache = new Map<DateIso, Seance[]>();
    return (d: DateIso) => { let s = cache.get(d); if (!s) { s = seancesDuJour(sources, d); cache.set(d, s); } return s; };
  }, [sources]);
  /** Les autres ateliers de la personne, pour « + Un autre de vos ateliers ce jour-là ». */
  const ajoutablesDe = useCallback((d: DateIso): Atelier[] => ateliersAjoutables(sources, d), [sources]);
  ctxRef.current = { aujourdhui, seances: seancesChoisies, personnes: personnesParId };

  // Les séances des jours choisis sont-elles toutes connues ? Tant que non, on ne conclut rien (« 0 séance », « Aucune séance à envoyer ») et « Envoyer » attend.
  const moisChoisisManquants = Array.from(new Set(etat.jours.map(moisDe))).filter((m) => !progParMois[m]);
  const donnees: EtatDonnees = useMemo(() => {
    if (absentId == null || etat.jours.length === 0) return { etat: 'pretes' };
    if (moisChoisisManquants.some((m) => erreurMois[m])) return { etat: 'erreur', txt: dire("Vos séances n'ont pas pu être chargées : cliquez sur « Réessayer ».", "Les séances n'ont pas pu être chargées : cliquez sur « Réessayer ».") };
    if (echec.demandes) return { etat: 'erreur', txt: dire("Vos demandes déjà faites n'ont pas pu être vérifiées : cliquez sur « Réessayer ».", "Les demandes déjà faites n'ont pas pu être vérifiées : cliquez sur « Réessayer ».") };
    if (moisChoisisManquants.length > 0 || !ateliersFinis || !existantesCouvrent) return { etat: 'chargement' };
    return { etat: 'pretes' };
  }, [absentId, etat.jours.length, moisChoisisManquants.join(','), erreurMois, echec.demandes, ateliersFinis, existantesCouvrent, dire]);   // eslint-disable-line react-hooks/exhaustive-deps

  const choixEtat = useMemo(() => ({ choix: etat.choix, off: etat.off }), [etat.choix, etat.off]);
  const cpt: Compteurs = useMemo(() => compteurs(seancesChoisies, choixEtat), [seancesChoisies, choixEtat]);
  const autreActivite = aUneAutreActivite(etat.notesJour, etat.jours);
  const lesManques: Manque[] = useMemo(() => manques({
    gestion: peutChoisirAutrui, absentId, jours: etat.jours, seances: seancesChoisies, e: choixEtat, personnes: personnesParId, autreActivite,
    motif: etat.motif, tipee: etat.tipee, donnees, voix: dire,
  }), [peutChoisirAutrui, absentId, etat.jours, seancesChoisies, choixEtat, personnesParId, autreActivite, etat.motif, etat.tipee, donnees, dire]);

  // ── Historique de remplacement, par atelier, à la demande ─────────────────────────────────────────────
  const [hist, setHist] = useState<Record<number, Record<number, HistoriqueRemplacement>>>({});
  const enCoursHist = useRef(new Set<number>());
  const atelierIds = useMemo(() => Array.from(new Set(seancesChoisies.map((s) => s.atelierId))).sort((a, b) => a - b), [seancesChoisies]);
  useEffect(() => {
    for (const id of atelierIds) {
      if (hist[id] || enCoursHist.current.has(id)) continue;
      enCoursHist.current.add(id);
      api.getHistoriqueRemplacements([id])
        .then((l) => setHist((h) => ({ ...h, [id]: Object.fromEntries(l.map((x) => [x.personne_id, x])) })))
        .catch((err: unknown) => console.error('[nouvelle demande] historique de remplacement', err))   // sans lui : « pas encore remplacé ici » pour tout le monde
        .finally(() => enCoursHist.current.delete(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atelierIds.join(',')]);

  /** Candidat·e·s classé·e·s pour une séance (disponibles d'abord) : le classement est informatif, jamais un filtre. */
  const candidatsDe = useCallback((s: Seance): Candidat[] => classer({
    personnes, absentId, avecExternes: peutChoisirAutrui, seance: s, atelier: ateliers[s.atelierId], hist: hist[s.atelierId] ?? {}, absences, absencesConnues: !echec.absences,
    seancesDuJour: programme.filter((p) => p.date === s.date), battementMin: reglages.battementMin,
  }), [personnes, absentId, peutChoisirAutrui, ateliers, hist, absences, echec.absences, programme, reglages.battementMin]);

  // ── Solde de vacances (Tipee) : tous les jours d'absence choisis, calculé après une courte pause (chaque geste appelle Tipee en mode réel) ──
  const [solde, setSolde] = useState<ControleSolde | null>(null);
  useEffect(() => {
    setSolde(null);
    if (etat.motif !== 'vacances' || absentId == null || etat.jours.length === 0) return;
    let annule = false;
    const t = setTimeout(() => {
      api.controleSolde(absentId, etat.jours).then((c) => { if (!annule) setSolde(c); })
        .catch((err: unknown) => console.error('[nouvelle demande] solde de vacances', err));   // le serveur revérifie le solde à l'envoi
    }, 500);
    return () => { annule = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etat.motif, absentId, etat.jours.join(',')]);

  // ── Envoi ──────────────────────────────────────────────────────────────────────────────────────────────
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avertSolde, setAvertSolde] = useState<ControleSolde | null>(null);
  const [resultat, setResultat] = useState<ResultatEnvoi | null>(null);
  const [aucuneNouvelle, setAucuneNouvelle] = useState<ResultatCreation | null>(null);
  // Une erreur d'envoi ne vaut que pour la demande telle qu'elle était : dès qu'elle change, le pied redit ce qui manque vraiment.
  useEffect(() => { setErreur(null); }, [etat]);

  const peutPrevenir = useCallback((id: number) => {
    const p = personnesParId[id];
    return !!p && reglages.scopeCollegues !== 'off' && peutEtrePrevenue(p, absentId);
  }, [personnesParId, reglages.scopeCollegues, absentId]);

  const envoyer = useCallback(async (horsSoldeConfirme = false): Promise<void> => {
    const e = etatRef.current;
    if (e.absentId == null || e.motif == null) return;
    setEnvoi(true); setErreur(null); setAucuneNouvelle(null);
    try {
      const corps = corpsCreation({
        absentId: e.absentId, jours: e.jours, seances: seancesChoisies, e: { choix: e.choix, off: e.off }, personnes: personnesParId, notesJour: e.notesJour,
        notes: e.notes, flex: e.flex, motif: e.motif, tipee: e.tipee, horsSoldeConfirme, peutPrevenir,
      });
      if (corps.ateliers.length === 0) { setErreur('Aucune séance à envoyer.'); return; }
      const res = await api.createDemandes(corps);
      setAvertSolde(null);
      if (res.creees.length > 0) setResultat({ ...res, dejaDemandees: seancesChoisies.filter((s) => s.existante || s.priseEnCharge) });
      else if (res.doublons.length > 0) setAucuneNouvelle(res);      // tout existait déjà : on le dit, rien n'a été fait
      else setErreur('La création de la demande a échoué.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.corps.code === 'hors_solde') setAvertSolde(err.corps.controle as ControleSolde);
      else { setAvertSolde(null); setErreur(texteErreur(err)); }   // l'erreur se lit à côté du bouton : la fenêtre du solde ne la cacherait pas
    } finally {
      setEnvoi(false);
    }
  }, [seancesChoisies, personnesParId, peutPrevenir]);

  /** Recharge les demandes déjà faites (après l'envoi d'un brouillon repéré dans Tipee, ou « Réessayer »). */
  const rechargerDonnees = useCallback(() => setVersionDonnees((v) => v + 1), []);

  /** Après l'envoi : « Nouvelle demande » repart d'une page vide (la personne absente reste la même pour une personne intervenante). */
  const reinitialiser = useCallback(() => {
    setResultat(null); setAucuneNouvelle(null); setAvertSolde(null); setErreur(null);
    setVersionDonnees((v) => v + 1);
    setEtat(etatInitial({ aujourdhui, absentId: peutChoisirAutrui ? null : maPersonne?.id ?? null }));
  }, [aujourdhui, peutChoisirAutrui, maPersonne]);

  return {
    // contexte
    aujourdhui, meta, peutChoisirAutrui, personnes, personnesParId, maPersonne, chargementMoi, personneAbsente, pourMoi, voix: laVoix, dire, reglages, erreurChargement,
    // saisie
    etat, dispatch, etatCourant, apresRendu,
    // programme, séances et ce qui n'a pas pu être chargé
    moisEnErreur, rechargerProgramme, programmeDuJour, donnees, echec, seancesDuJour: seancesDuJourMemo, seancesChoisies, choixEtat, compteurs: cpt, manques: lesManques,
    candidatsDe, ajoutablesDe,
    // solde
    solde,
    // envoi
    envoi, erreur, avertSolde, setAvertSolde, resultat, aucuneNouvelle, setAucuneNouvelle, envoyer, reinitialiser, rechargerDonnees,
  };
}

export type Nd = ReturnType<typeof useNouvelleDemande>;
